// shared/off-normalize.ts
//
// Pure Open Food Facts response normaliser, shared by the browser (which now
// fetches OFF directly — the Cloudflare Worker's datacentre egress IP gets
// rate-limited/blocked by OFF) and the server (kept for re-export + tests).
import type { NormalizedProduct, OffNutriments, OffProduct, OffResponse, ServingUnit } from "./types";
import { extractOffMicros } from "./micros";

/** OFF v2 product endpoint host. German subdomain biases product_name resolution to DE. */
export const OFF_HOST = "https://de.openfoodfacts.org";

/** Only the fields we read — keeps the payload small. */
export const OFF_FIELDS = [
  "code",
  "product_name",
  "product_name_de",
  "generic_name",
  "generic_name_de",
  "brands",
  "image_front_url",
  "image_front_small_url",
  "serving_size",
  "nutriments",
  "nutrition_data_per",
  "quantity",
  "product_quantity_unit",
].join(",");

export function offProductUrl(code: string): string {
  return `${OFF_HOST}/api/v2/product/${encodeURIComponent(code)}.json?fields=${OFF_FIELDS}`;
}

/** Legacy v0 product endpoint — the lenient last resort when every v2 code
 *  variant 404s. v0 always answers HTTP 200 and signals found/not-found in
 *  the body, which normalizeOff already understands (same shape as v2). */
export function offProductUrlV0(code: string): string {
  return `${OFF_HOST}/api/v0/product/${encodeURIComponent(code)}.json?fields=${OFF_FIELDS}`;
}

/** The code forms OFF might store a scanned barcode under, most likely first.
 *  OFF keeps whatever form the contributor uploaded: US products appear both
 *  as bare 12-digit UPC-A and as 0-padded EAN-13; small-pack EAN-8 codes
 *  appear both bare and zero-padded — and strict v2 only matches the stored
 *  form exactly (OFF's own scanner tries variants, so products "in the
 *  library" were invisible to us; owner bug report 2026-08-08). The scanned,
 *  normalized code stays the cache key — these are lookup forms only. */
export function codeVariants(code: string): string[] {
  const out = [code];
  if (code.length === 13 && code.startsWith("00000")) out.push(code.slice(5)); // zero-padded EAN-8
  else if (code.length === 13 && code.startsWith("0")) out.push(code.slice(1)); // UPC-A stored bare
  if (code.length === 8) out.push("00000" + code); // EAN-8 stored padded
  return [...new Set(out)];
}

const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === "string" ? parseFloat(v.replace(",", ".")) : (v as number);
  return Number.isFinite(n) ? n : null;
};

const round1 = (n: number | null): number | null =>
  n === null ? null : Math.round(n * 10) / 10;

export function parseServing(s: string | undefined | null): { size: number; unit: ServingUnit } | null {
  if (!s) return null;
  const m = s.match(/(\d+(?:[.,]\d+)?)\s*(g|ml)\b/i);
  if (!m) return null;
  return {
    size: Math.round(parseFloat(m[1].replace(",", "."))),
    unit: m[2].toLowerCase() === "ml" ? "ml" : "g",
  };
}

export function parseServingG(s: string | undefined | null): number | null {
  return parseServing(s)?.size ?? null;
}

function deriveKcal(n: OffNutriments): number | null {
  const direct = num(n["energy-kcal_100g"]);
  if (direct != null) return Math.round(direct);
  const kj = num(n["energy_100g"]);
  if (kj != null) return Math.round(kj / 4.184);
  return null;
}

/** Shape a single OFF product object into a NormalizedProduct (or null if
 *  unusable). `code` is passed separately because the search endpoint and the
 *  product endpoint surface it differently. */
export function normalizeOffProduct(code: string, p: OffProduct): NormalizedProduct | null {
  const name = (p.product_name_de || p.product_name || p.generic_name_de || p.generic_name || "").trim();
  const brand = (p.brands || "").split(",")[0]?.trim() || "";
  if (!name && !brand) return null;

  const n = p.nutriments ?? {};
  const kcal = deriveKcal(n);
  if (kcal == null) return null;

  const serving = parseServing(p.serving_size);
  // Liquids are often labeled per-100g by OFF even when the package is in
  // ml/l/cl — also read the printed package quantity so drinks default to ml.
  // The quantity regex must survive real OFF strings: "330 ml", "0.5 l",
  // "6x330ml" (no spaces), "1,5L", "1 Liter"/"litre" — while "2 lb", "500g",
  // "1 loaf" stay solid (the unit may not be followed by another letter).
  const liquidQuantity =
    ["ml", "cl", "l"].includes((p.product_quantity_unit ?? "").toLowerCase())
    || /\d+(?:[.,]\d+)?\s*(?:ml|cl|litres?|liters?|l)(?![a-z])/i.test(p.quantity ?? "");
  const unit: ServingUnit =
    (p.nutrition_data_per ?? "").toLowerCase().includes("ml") ? "ml"
    : serving?.unit === "ml" ? "ml"
    : liquidQuantity ? "ml"
    : "g";
  return {
    code,
    name: name || brand,
    brand: name ? brand : "",
    image_url: p.image_front_url || p.image_front_small_url || null,
    unit,
    serving_size_g: serving?.size ?? null,
    per_100g: {
      kcal,
      protein_g: round1(num(n["proteins_100g"])),
      carbs_g:   round1(num(n["carbohydrates_100g"])),
      sugar_g:   round1(num(n["sugars_100g"])),
      fat_g:     round1(num(n["fat_100g"])),
      sat_fat_g: round1(num(n["saturated-fat_100g"])),
      fiber_g:   round1(num(n["fiber_100g"])),
      salt_g:    round1(num(n["salt_100g"])),
      micros:    extractOffMicros(n as Record<string, unknown>),
    },
  };
}

/** Returns the normalised product on hit, or `null` on miss / unusable data. */
export function normalizeOff(code: string, raw: unknown): NormalizedProduct | null {
  const r = (raw ?? {}) as OffResponse;
  if (r.status !== 1 || !r.product) return null;
  return normalizeOffProduct(code, r.product);
}
